import React, { FC } from "react";

import { DataViewKey } from "@keepeek/commons";
import { useThemeColors } from "@keepeek/refront-components";
import { Container, Grid } from "@mui/material";
import clsx from "clsx";
import { isNil } from "lodash";
import { useRecoilValue } from "recoil";

import { Template } from "../../../../models/configuration/definitions/template";
import { widgetStatesAtom } from "../../atoms";
import { KWidgets } from "../../KWidgets";
import { WidgetConfigurations } from "../../models";
import { getWidgetsKeysTemplate } from "../../utils/getWidgetsKeysTemplates";
import { isWidgetsRendered } from "../../utils/isWidgetsRendered";
import StyledKTemplatesWrapper from "./index.styled";

export type KTemplateWrapperProps = {
  classNameIndex: number;
  template: Template;
  widgetsConfigurationPreloaded: WidgetConfigurations;
  dataViewKey: DataViewKey;
};
const KTemplateWrapper: FC<React.PropsWithChildren<KTemplateWrapperProps>> = function ({
  classNameIndex,
  template,
  widgetsConfigurationPreloaded,
  dataViewKey,
}) {
  const { convertColor } = useThemeColors();
  const widgetStates = useRecoilValue(widgetStatesAtom);

  const widgetsKey = getWidgetsKeysTemplate(template);
  const contentIsRendered = isWidgetsRendered(widgetsKey, widgetStates);
  const content = (
    <StyledKTemplatesWrapper
      padding={contentIsRendered ? template.padding : 0}
      margin={contentIsRendered ? template.margin : 0}
      className={clsx(
        "KTemplateWrapper",
        `KTemplateWrapper-index-${classNameIndex}`,
        template.classNames,
      )}
      // keep only the vertical
      style={{
        marginLeft: 0,
        marginRight: 0,
        paddingLeft: 0,
        paddingRight: 0,
        ...(template.autoTopMargin && {
          marginTop: "auto",
        }),
        flexGrow: template.verticallyExtendable ? 1 : 0,
        backgroundColor: convertColor(template.backgroundColor),
      }}
      animationType={template.animation?.animationType}
    >
      <Grid
        container
        className={`KTemplateWrapper-Grid KTemplateWrapper-Grid-${classNameIndex}`}
        direction="row"
        justifyContent="space-around"
        alignItems="stretch"
        spacing={template.gridSpacing}
        rowSpacing={template.rowSpacing}
        columnSpacing={template.columnSpacing}
      >
        {template.columns?.map((column, indexC) => {
          return (
            <Grid
              className={clsx(
                "KTemplateWrapper-GridItem",
                `KTemplateWrapper-GridItem-${classNameIndex}`,
                column.classNames,
              )}
              key={indexC}
              item
              xs={(column.breakpoints?.xs as any) || 12}
              sm={(column.breakpoints?.sm as any) || undefined}
              md={(column.breakpoints?.md as any) || undefined}
              lg={(column.breakpoints?.lg as any) || undefined}
              xl={(column.breakpoints?.xl as any) || undefined}
              sx={{
                backgroundColor: convertColor(column.backgroundColor),
                ...(!isNil(column.borderWidth) && {
                  borderWidth: `${column.borderWidth}px`,
                }),
                ...(column.borderColor && {
                  borderColor: `${convertColor(column.borderColor)}`,
                }),
                ...((!isNil(column.borderWidth) || column.borderColor) && {
                  borderStyle: "solid",
                }),
              }}
            >
              <KWidgets
                widgets={column.widgets}
                widgetConfigurations={widgetsConfigurationPreloaded}
                dataViewKey={dataViewKey}
              />
            </Grid>
          );
        })}
      </Grid>
    </StyledKTemplatesWrapper>
  );
  return template.container ? <Container>{content}</Container> : content;
};

export default KTemplateWrapper;
